"use client";

import { useRef, useState } from "react";
import { useAction, type ReactAction } from "convex/react";
import { api } from "@convex/_generated/api";
import { geocodePostalCode, type GeocodeResult } from "@/lib/geocoding/geocode";
import { handleError } from "@/lib/handle-error";
import type { PalletShippingMode } from "@shared/freight/shipping-mode";
import {
  freightLoad,
  freightQuoteArgs,
  isUsZip,
  palletShippingMode,
  type FreightLoad,
  type FreightQuoteInputs,
} from "./freight-quote-request";

export interface FreightQuote {
  displayPriceCents: number;
  transitDays: number | null;
  carrier: string | null;
  shippingMode: PalletShippingMode;
  palletCount: number;
  originZip: string;
  destZip: string;
  destPlace: GeocodeResult | null;
}

interface LanePlaces {
  origin: GeocodeResult | null;
  dest: GeocodeResult | null;
}

interface PublicQuoteOption {
  totalCents: number | null;
  transitDays: number | null;
  carrier: string | null;
}

type GetFreightQuote = ReactAction<typeof api.freight.actions.getFreightQuote>;

type LaneOutcome = { places: LanePlaces } & ({ quote: FreightQuote } | { error: string });

const LANE_END_BY_INPUT: Partial<Record<keyof FreightQuoteInputs, keyof LanePlaces>> = {
  originZip: "origin",
  destZip: "dest",
};

const INITIAL_INPUTS: FreightQuoteInputs = {
  originZip: "",
  destZip: "",
  pallets: "1",
  weight: "",
  equipmentType: "DRY",
};

function pricedOption(options: PublicQuoteOption[]): (PublicQuoteOption & { totalCents: number }) | null {
  const first = options[0];
  if (first === undefined || first.totalCents === null || first.totalCents <= 0) return null;
  return { ...first, totalCents: first.totalCents };
}

async function quoteLane(
  getFreightQuote: GetFreightQuote,
  lane: { originZip: string; destZip: string; load: FreightLoad },
): Promise<LaneOutcome> {
  const { originZip, destZip, load } = lane;
  const [origin, dest] = await Promise.all([geocodePostalCode(originZip), geocodePostalCode(destZip)]);
  const places = { origin, dest };
  const result = await getFreightQuote(
    freightQuoteArgs({ originZip, destZip, originPlace: origin, destPlace: dest, load, now: new Date() }),
  );
  const best = result.success ? pricedOption(result.options ?? []) : null;
  if (best === null) return { places, error: result.error ?? "No quotes available for this route" };
  return {
    places,
    quote: {
      displayPriceCents: best.totalCents,
      transitDays: best.transitDays,
      carrier: best.carrier,
      shippingMode: load.shippingMode,
      palletCount: load.palletCount,
      originZip,
      destZip,
      destPlace: dest,
    },
  };
}

function placeLabel(place: GeocodeResult | null): string | undefined {
  return place ? `${place.city}, ${place.state}` : undefined;
}

function useLaneQuote() {
  const [places, setPlaces] = useState<LanePlaces>({ origin: null, dest: null });
  const [quote, setQuote] = useState<FreightQuote | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const latestRequest = useRef(0);
  const getFreightQuote = useAction(api.freight.actions.getFreightQuote);

  function discard(changedEnd?: keyof LanePlaces) {
    latestRequest.current += 1;
    if (changedEnd) setPlaces((current) => ({ ...current, [changedEnd]: null }));
    setQuote(null);
    setError(null);
    setIsLoading(false);
  }

  async function request(lane: { originZip: string; destZip: string; load: FreightLoad }) {
    latestRequest.current += 1;
    const requestId = latestRequest.current;
    setIsLoading(true);
    setError(null);
    try {
      const outcome = await quoteLane(getFreightQuote, lane);
      if (latestRequest.current !== requestId) return;
      setPlaces(outcome.places);
      setQuote("quote" in outcome ? outcome.quote : null);
      setError("error" in outcome ? outcome.error : null);
    } catch (caught) {
      if (latestRequest.current !== requestId) return;
      const handled = handleError(caught, { display: "silent" });
      if (handled.action === "ignore") return;
      setError(handled.message);
      setQuote(null);
    } finally {
      if (latestRequest.current === requestId) setIsLoading(false);
    }
  }

  return { places, quote, isLoading, error, discard, request };
}

export function useFreightQuote() {
  const [inputs, setInputs] = useState<FreightQuoteInputs>(INITIAL_INPUTS);
  const laneQuote = useLaneQuote();
  const load = freightLoad(inputs);
  const canFetchQuote = isUsZip(inputs.originZip) && isUsZip(inputs.destZip) && load !== null;

  function update<Key extends keyof FreightQuoteInputs>(key: Key, value: FreightQuoteInputs[Key]) {
    setInputs((current) => ({ ...current, [key]: value }));
    laneQuote.discard(LANE_END_BY_INPUT[key]);
  }

  async function fetchQuote() {
    if (!canFetchQuote || load === null) return;
    await laneQuote.request({ originZip: inputs.originZip, destZip: inputs.destZip, load });
  }

  return {
    inputs,
    update,
    shippingMode: palletShippingMode(inputs.pallets),
    originLabel: placeLabel(laneQuote.places.origin),
    destLabel: placeLabel(laneQuote.places.dest),
    quote: laneQuote.quote,
    isLoading: laneQuote.isLoading,
    error: laneQuote.error,
    canFetchQuote,
    fetchQuote,
  };
}
